import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/cn';

/**
 * Written in the shadcn/ui idiom and owned in this repository, not installed.
 *
 * A button we own has no next major version to get stuck between.
 *
 * The Radix-backed primitives (dialog, select, combobox) are worth taking from
 * the shadcn CLI when a screen needs them — accessible focus management and
 * keyboard behaviour are not worth hand-rolling. A button is.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium ' +
    'transition-colors disabled:pointer-events-none disabled:opacity-50 ' +
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
        secondary: 'bg-bg-sunken text-fg border border-border-default hover:bg-bg-hover',
        ghost: 'text-fg-muted hover:bg-bg-hover hover:text-fg',
        danger: 'bg-danger text-accent-fg hover:opacity-90',
      },
      // On a touch screen every size grows to 44 px: a finger needs the room
      // (WCAG 2.5.5); a mouse does not, so the desktop layout stays as it is.
      size: {
        sm: 'h-8 px-3 pointer-coarse:h-11',
        md: 'h-9 px-4 pointer-coarse:h-11',
        // Icon-only buttons still need an accessible name — pass aria-label.
        icon: 'size-9 pointer-coarse:size-11',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ComponentProps<'button'> & VariantProps<typeof buttonVariants>;

export const Button = ({ className, variant, size, type = 'button', ...props }: ButtonProps) => (
  // type defaults to 'button': an unspecified button inside a form submits it,
  // which is a recurring source of accidental submissions.
  <button type={type} className={cn(buttonVariants({ variant, size }), className)} {...props} />
);
